import { useState, type ElementType } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Package,
  Box,
  Boxes,
  Users,
  UserCog,
  Building2,
  Warehouse,
  Factory,
  Truck,
  History,
  FlaskConical,
  Beaker,
  PackageMinus,
  PackagePlus,
  ClipboardList,
  Send,
  ClipboardCheck,
  Settings,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { api } from '../lib/api-client';
import { UpdateBanner } from './UpdateBanner';

interface NavItem {
  to: string;
  i18nKey: string;
  icon: ElementType;
  requiresAdmin?: boolean;
}

interface NavGroup {
  labelKey?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [{ to: '/', i18nKey: 'nav.dashboard', icon: LayoutDashboard }],
  },
  {
    labelKey: 'nav.group.anagrafiche',
    items: [
      { to: '/products', i18nKey: 'nav.products', icon: Package },
      { to: '/packaging-items', i18nKey: 'nav.packagingItems', icon: Box },
      { to: '/lots', i18nKey: 'nav.lots', icon: Boxes },
      { to: '/customers', i18nKey: 'nav.customers', icon: Users },
      { to: '/suppliers', i18nKey: 'nav.suppliers', icon: Building2 },
    ],
  },
  {
    labelKey: 'nav.group.magazzino',
    items: [
      { to: '/warehouse/main', i18nKey: 'nav.warehouseMain', icon: Warehouse },
      { to: '/warehouse/production', i18nKey: 'nav.warehouseProduction', icon: Factory },
      { to: '/warehouse/shipping', i18nKey: 'nav.warehouseShipping', icon: Truck },
      { to: '/stock-movements', i18nKey: 'nav.stockMovements', icon: History },
    ],
  },
  {
    labelKey: 'nav.group.produzione',
    items: [
      // Flusso operativo: lotto entra in produzione via SAP -> cernito con PSC ->
      // lavorato con LNP -> movimentato giornalmente con MPG -> confezionato in
      // scatole con CON -> spedito con Spedizioni (MSS Spagna / MSF Francia,
      // modulo unico guidato dalla destinazione) -> riconciliato con INV.
      { to: '/documents/sap', i18nKey: 'nav.sap', icon: PackageMinus },
      { to: '/documents/psc', i18nKey: 'nav.psc', icon: FlaskConical },
      { to: '/documents/lnp', i18nKey: 'nav.lnp', icon: Beaker },
      { to: '/documents/mpg', i18nKey: 'nav.mpg', icon: ClipboardList },
      { to: '/documents/con', i18nKey: 'nav.con', icon: PackagePlus },
      { to: '/documents/spedizioni', i18nKey: 'nav.spedizioni', icon: Send },
      { to: '/documents/inv', i18nKey: 'nav.inv', icon: ClipboardCheck },
    ],
  },
  {
    labelKey: 'nav.group.admin',
    items: [
      { to: '/admin/users', i18nKey: 'nav.users', icon: Users, requiresAdmin: true },
      { to: '/admin/operators', i18nKey: 'nav.operators', icon: UserCog, requiresAdmin: true },
      { to: '/settings', i18nKey: 'nav.settings', icon: Settings },
    ],
  },
];

function filterGroups(role: string | undefined): NavGroup[] {
  return NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.requiresAdmin || role === 'ADMIN'),
    }))
    .filter((g) => g.items.length > 0);
}

function SidebarNav({ groups, closeDrawer }: { groups: NavGroup[]; closeDrawer?: () => void }) {
  const { t } = useTranslation();
  return (
    <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4 space-y-5">
      {groups.map((group, idx) => (
        <div key={group.labelKey ?? `g-${idx}`}>
          {group.labelKey && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
              {t(group.labelKey)}
            </p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={closeDrawer}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-sm'
                        : 'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-sidebar-foreground'
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{t(item.i18nKey)}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SidebarContent({
  email,
  groups,
  onLogout,
  onClose,
}: {
  email: string | undefined;
  groups: NavGroup[];
  onLogout: () => void;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center justify-between px-4 py-4 border-b border-sidebar-border">
        <span className="text-base font-bold text-white">{t('app.title')}</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav.closeMenu')}
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground rounded p-1"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="px-4 py-3 border-b border-sidebar-border flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-sidebar-accent text-sidebar-accent-foreground flex items-center justify-center font-bold text-sm shrink-0">
          {email?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate text-sidebar-foreground">{email || '—'}</p>
        </div>
      </div>
      <SidebarNav groups={groups} closeDrawer={onClose} />
      <div className="p-3 border-t border-sidebar-border">
        <button
          type="button"
          onClick={() => {
            onLogout();
            onClose?.();
          }}
          className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-sidebar-foreground transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t('nav.logout')}
        </button>
      </div>
    </>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groups = filterGroups(user?.role);

  async function handleLogout() {
    try {
      await api<void>('/api/auth/logout', { method: 'POST', auth: false });
    } finally {
      clear();
    }
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:flex md:fixed md:inset-y-0 md:left-0 w-60 flex-col bg-sidebar text-sidebar-foreground">
        <SidebarContent email={user?.email} groups={groups} onLogout={handleLogout} />
      </aside>

      <div className="flex-1 min-w-0 md:pl-60">
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('nav.openMenu')}
            className="text-slate-700 hover:text-slate-900 rounded p-1"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-brand-700">{t('app.title')}</h1>
        </header>

        <main className="mx-auto w-full max-w-7xl px-6 py-6">
          <Outlet />
        </main>
      </div>

      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative w-72 max-w-[80vw] flex flex-col bg-sidebar text-sidebar-foreground shadow-xl">
            <SidebarContent
              email={user?.email}
              groups={groups}
              onLogout={handleLogout}
              onClose={() => setDrawerOpen(false)}
            />
          </aside>
        </div>
      )}

      <UpdateBanner />
    </div>
  );
}
